import {
    action,
    configure,
    decorate,
    observable,
    runInAction,
} from "mobx";

configure({ enforceActions: "always" });

class VirtualAuctionStore {
    constructor() {
        this.initialize();
    }

    initialize = () =>
        runInAction(() => {
            this.activeLaneID = null;
            this.countdownKey = 1;
            this.isCountdownActive = false;
            this.isBonusTimeActive = false;

            this.virtualLaneList = [];
            this.auction = {};
            this.bidStatusCode = null;
            this.bidStatusMessage = null;

            this.isPaused = false;
        });

    // --- Countdown logic ---
    resetCountdown = (duration, laneID = null) => {
        this.stopCountdown(laneID);
        this.startCountdown(duration, laneID);
        runInAction(() => {
            this.countdownKey = this.countdownKey + 1;
        });
    };

    getRemainingTime = () => {
        if (!this.countdownStartTime || !this.duration) return 0;
        const elapsed = (Date.now() - this.countdownStartTime) / 1000;
        return Math.max(0, this.duration - elapsed);
    };

    startCountdown = (duration, laneID = null) => {
        runInAction(() => {
            this.isCountdownActive = true;
            this.isBonusTimeActive = false;
            this.duration = duration;
            this.countdownStartTime = Date.now();
        });
        this.startTimer(duration);
    };

    startBonusTime = (duration, laneID = null) => {
        runInAction(() => {
            this.isBonusTimeActive = true;
            this.isCountdownActive = true;
            this.duration = duration;
            this.countdownStartTime = Date.now();
        });
        this.startTimer(duration);
    };

    stopCountdown = (laneID = null) => {
        runInAction(() => {
            this.isCountdownActive = false;
            this.isBonusTimeActive = false;
            this.duration = 0;
            this.countdownStartTime = null;
        });

        if (this.countdownTimer) clearTimeout(this.countdownTimer);
        this.countdownTimer = null;
    };

    startTimer = (duration) => {
        if (this.countdownTimer) {
            clearTimeout(this.countdownTimer);
        }
        this.countdownTimer = setTimeout(() => {
            if (this.isCountdownActive && !this.isPaused) {
                runInAction(() => {
                    this.stopCountdown();
                });
            }
        }, duration * 1000);
    };

    // --- Pause state ---
    setLanePaused = (paused) => {
        runInAction(() => { this.isPaused = paused; });

        if (paused && this.countdownTimer) {
            clearTimeout(this.countdownTimer);
            this.countdownTimer = null;
        }
    };

    // --- Lane / Auction state ---
    setCurrentLaneAudio = (activeLaneID, value, isVirtualLane) => {
        runInAction(() => {
            if (!this.auction[activeLaneID]) {
                this.auction[activeLaneID] = {};
            }
            this.auction[activeLaneID].audioPlaying = value;
            this.auction[activeLaneID].isVirtual = isVirtualLane;
            this.activeLaneID = activeLaneID;
        });
    };

    // --- Misc ---
    setProperty = (key, value) => {
        runInAction(() => {
            this[key] = value;
        });
    };

    resetBidStatus = () => {
        this.setProperty("bidStatusCode", null);
        this.setProperty("bidStatusMessage", null);
    };

    resetBonusTime = (duration, laneID = null) => {
        this.stopCountdown(laneID);
        this.startBonusTime(duration, laneID);
        runInAction(() => {
            this.countdownKey = this.countdownKey + 1;
        });
    };
}

decorate(VirtualAuctionStore, {
    resetCountdown: action,
    resetBonusTime: action,
    resetBidStatus: action,
    startCountdown: action,
    startBonusTime: action,
    stopCountdown: action,
    isCountdownActive: observable,
    isBonusTimeActive: observable,
    duration: observable,
    countdownKey: observable,
    setCurrentLaneAudio: action,
    setLanePaused: action,
    setProperty: action,
    isPaused: observable,
    auction: observable,
    activeLaneID: observable,
    virtualLaneList: observable,
    bidStatusCode: observable,
    bidStatusMessage: observable,
});

export default VirtualAuctionStore;
